import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Anchor, Home as HomeIcon, Sun, Compass } from "lucide-react";
import { SITE, PHOTOS, MARINA_SERVICES, ACTIVITIES } from "../data/content";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
        <img
          src={SITE.hero}
          alt="Aerial view of Elk Creek Marina on Lake Tenkiller"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Soft side vignette — keeps drone shot bright; darkens only where text sits */}
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,24,42,0.78)] via-[rgba(8,24,42,0.25)] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[rgba(8,24,42,0.7)] to-transparent" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[rgba(8,24,42,0.4)] to-transparent" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-44 pb-28 min-h-[100svh] flex flex-col justify-end lg:justify-center">
          <div className="max-w-3xl">
            <div className="fade-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/12 backdrop-blur-md border border-white/25 text-white text-xs uppercase tracking-[0.22em]">
              <Sun className="w-3.5 h-3.5" /> Lake Tenkiller, Oklahoma
            </div>
            <h1 className="fade-up fade-up-2 font-display text-white mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" style={{ textShadow: "0 2px 32px rgba(8,24,42,0.95), 0 0 18px rgba(8,24,42,0.6)" }}>
              Elk Creek Marina
            </h1>
            <p className="fade-up fade-up-3 mt-6 text-white text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ textShadow: "0 1px 16px rgba(8,24,42,0.95), 0 0 10px rgba(8,24,42,0.6)" }}>
              A quiet, family-friendly lake retreat — cabins, boat slips and 130 miles of Tenkiller shoreline waiting just past the dock.
            </p>
            <div className="fade-up fade-up-3 mt-9 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-book-button"
                className="inline-flex items-center gap-2 bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Book Your Stay <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/cabins"
                data-testid="hero-cabins-button"
                className="inline-flex items-center gap-2 bg-white/95 hover:bg-white text-[var(--ec-navy)] font-semibold px-7 py-4 rounded-full btn-lift"
              >
                View Cabins
              </Link>
              <Link
                to="/marina"
                data-testid="hero-marina-button"
                className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Explore the Marina
              </Link>
              <a
                href={SITE.directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-directions-button"
                className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
              >
                <MapPin className="w-4 h-4" /> Get Directions<span className="sr-only"> (opens in new tab)</span>
              </a>
            </div>
          </div>

          {/* Scroll hint */}
          <div className="hidden lg:flex absolute bottom-10 right-10 items-center gap-3 text-white/70 text-xs uppercase tracking-[0.22em] fade-up fade-up-3">
            <span className="w-12 h-px bg-white/50" /> Scroll
          </div>
        </div>
      </section>

      {/* WELCOME / INTRO */}
      <section className="relative py-24 lg:py-32 bg-[var(--ec-cream)]" data-testid="welcome-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake)] mb-4">
              Welcome to Elk Creek
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-[var(--ec-navy)]">
              A more private, serene side of Lake Tenkiller.
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-[var(--ec-charcoal)]/80">
              We're a family-friendly resort and marina tucked into the north end of Lake Tenkiller — 12,900 acres of blue water and 130 miles of shoreline that has been welcoming guests since 1953. Whether you're after a quiet weekend on the deck or a long day on the water with friends, Elk Creek is the kind of place you exhale the moment you arrive.
            </p>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={150}>
            <div className="grid grid-cols-2 gap-4">
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[3/4]">
                <img src={PHOTOS.lakeSunset} alt="Sunset over Lake Tenkiller" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[3/4] mt-10">
                <img src={PHOTOS.lakeHills} alt="Lake Tenkiller hills" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUICK PICK: Cabins / Marina */}
      <section className="py-24 bg-white" data-testid="quick-pick-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="Choose your visit"
            title="What brings you out to the lake?"
            subtitle="Two ways to enjoy Elk Creek — settle into a stocked cabin, or roll right up to the dock. Either way, we'll see you on the water."
          />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Cabins card */}
            <Reveal>
              <Link
                to="/cabins"
                data-testid="quick-pick-cabins"
                className="group block relative rounded-3xl overflow-hidden card-lift shadow-md"
              >
                <div className="img-zoom aspect-[4/3] sm:aspect-[5/4]">
                  <img src={PHOTOS.cabinRow} alt="Elk Creek cabin row" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ec-navy-deep)] via-[rgba(15,42,71,0.55)] to-[rgba(15,42,71,0.15)]" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 text-white">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] uppercase tracking-[0.22em] mb-3 sm:mb-4">
                    <HomeIcon className="w-3.5 h-3.5" /> Cabin Rentals
                  </div>
                  <h3 className="font-display text-xl sm:text-3xl lg:text-4xl leading-tight">Stay in a stocked cabin steps from the dock.</h3>
                  <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/85 max-w-md">
                    Clean, comfortable cabins that sleep up to 8 — bedding, cookware and entertainment deck included.
                  </p>
                  <span className="mt-4 sm:mt-5 inline-flex items-center gap-2 font-semibold text-sm sm:text-base">
                    View Cabins <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>

            {/* Marina card */}
            <Reveal delay={120}>
              <Link
                to="/marina"
                data-testid="quick-pick-marina"
                className="group block relative rounded-3xl overflow-hidden card-lift shadow-md"
              >
                <div className="img-zoom aspect-[4/3] sm:aspect-[5/4]">
                  <img src={PHOTOS.heroDrone} alt="Elk Creek Marina aerial view" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ec-navy-deep)] via-[rgba(15,42,71,0.55)] to-[rgba(15,42,71,0.15)]" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 text-white">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] uppercase tracking-[0.22em] mb-3 sm:mb-4">
                    <Anchor className="w-3.5 h-3.5" /> Marina Services
                  </div>
                  <h3 className="font-display text-xl sm:text-3xl lg:text-4xl leading-tight">Slips, gas dock & lake access at the north end.</h3>
                  <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/85 max-w-md">
                    Day slips, yearly rentals, gas dock, convenience store and boat & paddleboard rentals.
                  </p>
                  <span className="mt-4 sm:mt-5 inline-flex items-center gap-2 font-semibold text-sm sm:text-base">
                    Explore the Marina <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARINA SERVICES */}
      <section className="py-24 bg-[var(--ec-sand-soft)]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="On the water"
            title="Everything you need to enjoy Tenkiller."
            subtitle="From a quick fuel-up to a full season at the dock, we've kept it simple so more of your time is spent on the lake."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MARINA_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <div
                  className="h-full bg-white rounded-2xl p-7 border border-[var(--ec-sand)] card-lift"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-full bg-[var(--ec-lake)]/10 flex items-center justify-center mb-5">
                    <Anchor className="w-5 h-5 text-[var(--ec-lake)]" />
                  </div>
                  <h3 className="font-display text-2xl text-[var(--ec-navy)]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ec-charcoal)]/75">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVITIES STRIP */}
      <section className="py-24 bg-white" data-testid="activities-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake)] mb-3">
              Things to do
            </div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] text-[var(--ec-navy)]">
              An honest lake day, the way they used to be.
            </h2>
            <p className="mt-6 text-[var(--ec-charcoal)]/75 leading-relaxed">
              Swim in the cove, throw a line in, paddle out at sunset, or just listen to the water from the deck. Lake Tenkiller has 130 miles of shoreline, plenty of coves, and even the original sunken town of Cookson for scuba divers.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {ACTIVITIES.map((a) => (
                <span
                  key={a}
                  className="text-xs px-3 py-1.5 rounded-full bg-[var(--ec-foam)] text-[var(--ec-navy)] font-medium"
                  data-testid={`activity-tag-${a.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {a}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={150}>
            <div className="grid grid-cols-6 grid-rows-2 gap-3 h-[420px]">
              <div className="col-span-3 row-span-2 img-zoom rounded-2xl overflow-hidden shadow-md card-lift">
                <img src={PHOTOS.tubing} alt="Tubing on Lake Tenkiller" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-3 row-span-1 img-zoom rounded-2xl overflow-hidden shadow-md card-lift">
                <img src={PHOTOS.pontoonGroup} alt="Pontoon ride on Tenkiller" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-3 row-span-1 img-zoom rounded-2xl overflow-hidden shadow-md card-lift">
                <img src={PHOTOS.kayakYellow} alt="Kayaking the cove" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LOCATION HIGHLIGHT */}
      <section className="relative py-28 overflow-hidden" data-testid="location-section">
        <img
          src={PHOTOS.lakeSunset}
          alt="Lake Tenkiller sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[rgba(8,24,42,0.82)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,24,42,0.4)] via-transparent to-[rgba(8,24,42,0.5)]" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-10 text-center text-white">
          <Reveal>
            <Compass className="w-8 h-8 mx-auto text-[var(--ec-lake-light)] mb-5" />
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white" style={{ textShadow: "0 2px 24px rgba(8,24,42,0.8)" }}>
              The quiet, blue-water side of Tenkiller.
            </h2>
            <p className="mt-6 text-lg text-white max-w-2xl mx-auto" style={{ textShadow: "0 1px 12px rgba(8,24,42,0.85)" }}>
              We're on the east side of the lake, just south of the major bridge that crosses the north end of Tenkiller — easy off Highway 82 and tucked into a park-like point.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <a
                href={SITE.directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-button"
                className="inline-flex items-center gap-2 bg-white text-[var(--ec-navy)] font-semibold px-7 py-4 rounded-full btn-lift"
              >
                <MapPin className="w-4 h-4" /> Get Directions<span className="sr-only"> (opens in new tab)</span>
              </a>
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="location-call-button"
                className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
              >
                <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BOOK */}
      <section className="py-24 bg-[var(--ec-navy)] text-white" data-testid="cta-book-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <Reveal>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake-light)] mb-4">
              Ready when you are
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              Plan your stay at Elk Creek.
            </h2>
            <p className="mt-6 text-white/75 text-lg max-w-2xl mx-auto">
              Cabins fill quickly on weekends and holidays. Reserve a cabin, secure a slip or just give us a call — we'll help you put together a great Tenkiller weekend.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link
                to="/contact"
                data-testid="bottom-book-cta"
                className="inline-flex items-center gap-2 bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Book Your Stay <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="bottom-contact-cta"
                className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
